import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const scoreItems = [
  { label: 'Population Growth', percent: 20, color: '#22C55E' },
  { label: 'Utility Access', percent: 20, color: '#FF8C20' },
  { label: 'Highway Access', percent: 15, color: '#3B82F6' },
  { label: 'New Development', percent: 15, color: '#A855F7' },
  { label: 'Appreciation Trends', percent: 15, color: '#FF8C20' },
  { label: 'Risk Factors', percent: 15, color: '#06B6D4' },
];

export default function ScoringBreakdown() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const textContent = section.querySelector('.text-content');
    const bars = section.querySelectorAll('.score-bar-fill');

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: 'top 75%',
        once: true,
      },
    });

    if (textContent) {
      tl.fromTo(
        textContent,
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8, ease: 'power3.out' }
      );
    }

    tl.fromTo(
      bars,
      { width: '0%' },
      { width: (i) => `${scoreItems[i].percent}%`, duration: 1.0, stagger: 0.1, ease: 'power2.out' },
      '-=0.4'
    );

    return () => {
      tl.kill();
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      id="scoring"
      style={{ background: '#07110D', padding: '120px 0' }}
    >
      <div className="mx-auto flex max-w-[1280px] flex-col items-center gap-16 px-6 md:flex-row">
        {/* Score bars */}
        <div className="md:w-[55%]">
          <div className="flex flex-col gap-6">
            {scoreItems.map((item) => (
              <div key={item.label}>
                <div className="mb-2 flex items-center justify-between">
                  <span
                    className="font-body font-medium"
                    style={{ fontSize: 14, color: '#F7F8F4' }}
                  >
                    {item.label}
                  </span>
                  <span
                    className="font-mono"
                    style={{ fontSize: 14, color: '#A8B8AE' }}
                  >
                    {item.percent}%
                  </span>
                </div>
                <div
                  className="w-full overflow-hidden rounded-full"
                  style={{ height: 8, background: 'rgba(255,255,255,0.1)' }}
                >
                  <div
                    className="score-bar-fill h-full rounded-full"
                    style={{
                      width: '0%',
                      background: item.color,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Text content */}
        <div className="text-content opacity-0 md:w-[40%]">
          <span className="label-accent block" style={{ color: '#7CFF6B' }}>
            HOW PARCEL LEAD PRO SCORES LAND
          </span>
          <h2
            className="font-display mt-4 font-semibold"
            style={{ fontSize: 'clamp(36px, 4vw, 48px)', lineHeight: 1.1, color: '#F7F8F4' }}
          >
            A proprietary scoring system.
          </h2>
          <p
            className="font-body mt-5"
            style={{ fontSize: 16, lineHeight: 1.6, color: '#A8B8AE' }}
          >
            We analyze population growth, highway access, utility availability,
            new development, appreciation trends, and flood risk — giving each
            parcel a 0-100 investment score so you know exactly where to focus.
          </p>
        </div>
      </div>
    </section>
  );
}
